package org.dronda.app.web.ui.eventcreate

import org.dronda.api.wishlist.EventLimits
import org.dronda.service.wishlist.core.domain.EventMode
import org.dronda.service.wishlist.core.domain.SantaExclusionDirection
import org.dronda.api.wishlist.EventRequest
import org.dronda.app.web.ui.eventcreate.CreateEventViewModel.Step

internal fun stepsFor(mode: EventMode): List<Step> = when (mode) {
    EventMode.SECRET_SANTA -> listOf(Step.Basics, Step.People, Step.Exclusions, Step.Review)
    EventMode.STANDARD -> listOf(Step.Basics, Step.People, Step.Review)
}

internal data class BasicsErrors(val name: String?, val date: String?, val budget: String?) {
    val hasErrors: Boolean get() = name != null || date != null || budget != null
}

internal fun validateBasics(name: String, date: String, budget: String): BasicsErrors = BasicsErrors(
    name = when {
        name.isBlank() -> "Give the event a name"
        EventRequest.nameValidation(name.trim()).isErr -> "Names can be at most 100 characters"
        else -> null
    },
    date = if (date.isBlank() || EventRequest.dateValidation(date).isErr) "Pick a date" else null,
    budget = if (budget.isNotBlank() && EventRequest.budgetValidation(budget.trim()).isErr) {
        "Budgets can be at most 50 characters"
    } else {
        null
    },
)

internal data class AddEmailsResult(val added: List<String>, val error: String?)

/**
 * Accepts one or many emails separated by commas, semicolons, or whitespace, so a pasted list works.
 */
internal fun addEmails(draft: String, existing: List<String>): AddEmailsResult {
    val candidates = draft.split(',', ';', ' ', '\n', '\t').map { it.trim() }.filter { it.isNotEmpty() }
    if (candidates.isEmpty()) return AddEmailsResult(emptyList(), null)

    val invalid = candidates.filter { EventRequest.participantEmailValidation(it).isErr }
    if (invalid.isNotEmpty()) return AddEmailsResult(emptyList(), "Not a valid email: ${invalid.joinToString()}")

    val known = existing.map { it.lowercase() }.toMutableSet()
    val added = candidates.filter { known.add(it.lowercase()) }
    val room = EventLimits.MAX_PARTICIPANTS - existing.size
    return if (added.size > room) {
        AddEmailsResult(added.take(room), "Events can have at most ${EventLimits.MAX_PARTICIPANTS} people")
    } else {
        AddEmailsResult(added, null)
    }
}

internal data class ExclusionDraft(val a: String, val b: String, val direction: SantaExclusionDirection) {
    fun matches(first: String, second: String): Boolean = (a == first && b == second) || (a == second && b == first)

    fun involves(email: String): Boolean = a == email || b == email
}

internal fun santaWarnings(mode: EventMode, people: List<String>, exclusions: List<ExclusionDraft>): List<String> {
    if (mode != EventMode.SECRET_SANTA) return emptyList()
    val warnings = mutableListOf<String>()
    if (people.size < EventLimits.MIN_SANTA_PARTICIPANTS) {
        warnings += "Secret Santa needs at least ${EventLimits.MIN_SANTA_PARTICIPANTS} people"
    }
    val blocked = exclusions.flatMapTo(mutableSetOf()) { it.direction.blockedDraws(it.a, it.b) }
    people.forEach { person ->
        when (people.count { it != person && (person to it) !in blocked }) {
            0 -> warnings += "$person can't draw anyone with these exclusions"
            1 -> warnings += "$person can only draw 1 person"
        }
        if (people.size > 1 && people.none { it != person && (it to person) !in blocked }) {
            warnings += "Nobody can draw $person with these exclusions"
        }
    }
    return warnings
}

internal fun reviewLines(
    name: String,
    date: String,
    mode: EventMode,
    budget: String,
    peopleCount: Int,
    exclusionCount: Int,
): List<String> = buildList {
    add("Name: ${name.trim()}")
    add("Date: $date")
    add(if (mode == EventMode.SECRET_SANTA) "Type: Secret Santa" else "Type: Wishlists")
    if (mode == EventMode.SECRET_SANTA && budget.isNotBlank()) add("Budget: ${budget.trim()}")
    add("People: $peopleCount (including you)")
    if (mode == EventMode.SECRET_SANTA) add("Exclusions: $exclusionCount")
}
